"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trackReviewSubmission } from "@/lib/services/analytics";
import { createClient } from "@/lib/supabase/client";
import { trpc } from "@/lib/trpc/react";

/**
 * Real-time supporting set review hook
 *
 * Follows the same pattern as useRealtimeShowReview:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed
 */

const SUPPORTING_SET_REVIEW_EVENT = "supporting-set-review-update";

export interface SupportingSetReview {
	id: string;
	showId: string;
	showArtistId: string;
	artistId: string;
	userId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
	createdAt: string;
	updatedAt: string;
}

interface UseRealtimeSupportingSetReviewOptions {
	showId: string;
	showArtistId: string;
	artistId: string;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeSupportingSetReview({
	showId,
	showArtistId,
	artistId,
	userId,
	enabled = true,
}: UseRealtimeSupportingSetReviewOptions) {
	const [review, setReview] = useState<SupportingSetReview | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for saving reviews
	const saveReviewMutation = trpc.show.saveSupportingSetReview.useMutation({
		onSuccess: async () => {
			const FORCE_REFETCH = { refetchType: 'all' as const };
			await Promise.all([
				utils.show.getUserSupportingSetReview.invalidate({ showArtistId }),
				utils.show.getSupportingSetReviews.invalidate({ showArtistId }),
				utils.show.getFacepileData.invalidate(),
				utils.show.list.invalidate(),
				utils.show.getBySlug.invalidate(),
				utils.show.getSupportingSetReviewStats.invalidate({ showArtistId }),
				...(artistId ? [
					utils.artist.getStats.invalidate({ artistId }),
					utils.artist.getShows.invalidate(undefined, FORCE_REFETCH),
					utils.artist.getCounts.invalidate({ artistId }),
				] : []),
				utils.user.getProfileShows.invalidate(undefined, FORCE_REFETCH),
				utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
			]);
		},
		onError: (err) => {
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to save your review"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to save review";
			toast.error(errorMessage);
		},
	});

	// Initial data fetch - runs when showArtistId changes (e.g., switching artists)
	useEffect(() => {
		let cancelled = false;

		if (!userId || !showArtistId || !enabled) {
			setIsLoading(false);
			setReview(null);
			return;
		}

		async function fetchInitialReview() {
			if (!userId) return;

			try {
				const { data, error } = await supabase
					.from("show_supporting_set_reviews")
					.select("*")
					.eq("user_id", userId)
					.eq("show_artist_id", showArtistId)
					.maybeSingle() as { data: any; error: any };

				if (cancelled) return;

				if (!error && data) {
					setReview({
						id: data.id,
						showId: data.show_id,
						showArtistId: data.show_artist_id,
						artistId: data.artist_id,
						userId: data.user_id,
						rating: data.rating,
						content: data.content,
						loved: data.loved,
						sawLive: data.saw_live,
						sawStream: data.saw_stream,
						wantToSee: data.want_to_see,
						mustSee: data.must_see,
						createdAt: data.created_at,
						updatedAt: data.updated_at,
					});
				} else {
					setReview(null);
				}
			} catch (error) {
				if (cancelled) return;
				console.error("Error fetching initial supporting set review:", error);
				setReview(null);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		fetchInitialReview();
		return () => { cancelled = true; };
	}, [userId, showArtistId, enabled, supabase]);

	// Real-time subscription
	useEffect(() => {
		if (!userId || !showArtistId || !enabled) return;

		const channelName = `supporting-set-review:${showArtistId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "show_supporting_set_reviews",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<any>) => {
					// Client-side filter for show_artist_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if (record?.show_artist_id !== showArtistId) {
						return;
					}

					// Delay to let optimistic updates take precedence
					setTimeout(() => {
						if (payload.eventType === "DELETE") {
							setReview(null);
						} else if (payload.new) {
							setReview({
								id: payload.new.id,
								showId: payload.new.show_id,
								showArtistId: payload.new.show_artist_id,
								artistId: payload.new.artist_id,
								userId: payload.new.user_id,
								rating: payload.new.rating,
								content: payload.new.content,
								loved: payload.new.loved,
								sawLive: payload.new.saw_live,
								sawStream: payload.new.saw_stream,
								wantToSee: payload.new.want_to_see,
								mustSee: payload.new.must_see,
								createdAt: payload.new.created_at,
								updatedAt: payload.new.updated_at,
							});
						}
					}, 1000);
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, showArtistId, enabled, supabase]);

	// Listen for optimistic updates from other component instances
	useEffect(() => {
		if (!userId || !showArtistId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				showArtistId: eventShowArtistId,
				review: eventReview,
			} = event.detail;

			if (eventUserId === userId && eventShowArtistId === showArtistId) {
				setReview(eventReview);
			}
		};

		window.addEventListener(
			SUPPORTING_SET_REVIEW_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				SUPPORTING_SET_REVIEW_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, showArtistId]);

	// Ref-based debounce for server mutations — stable across re-renders.
	const pendingMutationRef = useRef<any>(null);
	const mutationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const flushMutation = useCallback(async () => {
		const data = pendingMutationRef.current;
		pendingMutationRef.current = null;
		if (!data) return;
		try {
			await saveReviewMutation.mutateAsync(data);
			if (data.rating > 0) {
				trackReviewSubmission(showId, data.rating, "show", artistId, !!data.content?.trim());
			}
		} catch {
			// Error handled in mutation onError / realtime will correct
		}
	}, [saveReviewMutation, showId, artistId]);

	const debounceMutation = useCallback((mutationData: any) => {
		pendingMutationRef.current = mutationData;
		if (mutationTimerRef.current) clearTimeout(mutationTimerRef.current);
		mutationTimerRef.current = setTimeout(flushMutation, 800);
	}, [flushMutation]);

	// Save review with optimistic updates
	const saveReview = async (data: {
		rating?: number;
		content?: string;
		loved?: boolean;
		sawLive?: boolean;
		sawStream?: boolean;
		wantToSee?: boolean;
		mustSee?: boolean;
	}, { immediate = false }: { immediate?: boolean } = {}) => {
		if (!userId) {
			toast.error("Please sign in to save your review");
			return;
		}

		// Merge with existing review data
		const updatedReview = review
			? {
					...review,
					...data,
					updatedAt: new Date().toISOString(),
				}
			: {
					id: `temp-${showArtistId}`,
					showId,
					showArtistId,
					artistId,
					userId,
					rating: data.rating || 0,
					content: data.content || null,
					loved: data.loved || false,
					sawLive: data.sawLive || false,
					sawStream: data.sawStream || false,
					wantToSee: data.wantToSee || false,
					mustSee: data.mustSee || false,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};

		// Check if this should delete the review
		const shouldDelete =
			updatedReview.rating === 0 &&
			!updatedReview.content?.trim() &&
			!updatedReview.loved &&
			!updatedReview.sawLive &&
			!updatedReview.sawStream &&
			!updatedReview.wantToSee &&
			!updatedReview.mustSee;

		// 1. Optimistic update
		const optimisticReview = shouldDelete ? null : updatedReview;
		setReview(optimisticReview);

		// 2. Broadcast to other instances
		window.dispatchEvent(new CustomEvent(SUPPORTING_SET_REVIEW_EVENT, {
			detail: { userId, showArtistId, review: optimisticReview },
		}));

		// 3. Send to database (debounced for rapid toggles, immediate for modal saves)
		const mutationData = {
			showId,
			showArtistId,
			artistId,
			rating: updatedReview.rating,
			content: updatedReview.content || "",
			loved: updatedReview.loved,
			sawLive: updatedReview.sawLive,
			sawStream: updatedReview.sawStream,
			wantToSee: updatedReview.wantToSee,
			mustSee: updatedReview.mustSee,
		};

		if (immediate) {
			if (mutationTimerRef.current) {
				clearTimeout(mutationTimerRef.current);
				pendingMutationRef.current = null;
			}
			try {
				await saveReviewMutation.mutateAsync(mutationData);
				if (!shouldDelete && updatedReview.rating > 0) {
					trackReviewSubmission(showId, updatedReview.rating, "show", artistId, !!updatedReview.content?.trim());
				}
			} catch {
				// Error handled in mutation onError
			}
		} else {
			debounceMutation(mutationData);
		}
	};

	return {
		review,
		saveReview,
		isLoading,
		isPending: saveReviewMutation.isPending,
	};
}